import { useIntersectionObserver } from "@uidotdev/usehooks";
import Cookies from "js-cookie";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import {
  convertGptSummary,
  truncateBenefits,
  truncateSkills,
} from "../../../functions/job";
import { formatSalary } from "../../../helpers/conditional_rendering";
import { getCompanyLogoImage } from "../../../helpers/data_management";
import { sendTrackingEvent } from "../../../helpers/tracking_management";
import {
  bookmarkJob,
  unBookmarkJob,
  undoHideJob,
  updateFeedbackHideJob,
  updateHideJob,
} from "../../../redux/actions/job_action";
import * as types from "../../../redux/types/job_type";
import {
  BoxContainer,
  CheckboxButton,
  CheckboxContainer,
  CheckboxHover,
  CheckboxIcon,
  CheckedBox,
  CloseButton,
  UncheckedBox,
} from "../styles";
import {
  AiSummaryContainer,
  AiSummaryHeaderText,
  AiSummaryText,
  BookmarkButton,
  CardContainer,
  CardWrapper,
  CloseIcon,
  CompanyLogoImage,
  CompanyNameText,
  DescriptionContainer,
  DetailChip,
  DetailContainer,
  DividerStyled,
  FooterContainer,
  HeaderImage,
  HideButton,
  HideButtonContainer,
  SummaryBulletItem,
  SummaryBulletList,
  HideContentText,
  HideHeaderText,
  HideIcon,
  HideJobContainer,
  ImageHeaderContainer,
  JobDetailsText,
  JobHeaderContainer,
  JobTitleText,
  LeftArrowIcon,
  MoreDetailButton,
  OptionContainer,
  OptionLabel,
  RadioStyled,
  SaveIcon,
  ScrollContainer,
  ShowMoreText,
  TextButton,
  UnsaveIcon,
} from "./styles";

export default function ForYouJobCard(props) {
  const { job, index, setInView, selectedJob, handleSelectJob, handleHideJob } =
    props;

  const [hide, setHide] = useState(false);

  const [ref, entry] = useIntersectionObserver({
    threshold: 1,
  });

  function trackImpressionInView(job, index) {
    const currency = process.env.NEXT_PUBLIC_JSW_GEOLOCATION ? "SGD" : "RM";

    // POSTHOG JOB IMPRESSION TRACKING

    sendTrackingEvent({
      event: "CE_job-impression",
      "job-id": job?.id,
      origin: "fyp_page",
      job_discovery: "rec_profile",
      search_term: "",
    });
  }

  useEffect(() => {
    if (entry?.isIntersecting) {
      setInView(index);
      trackImpressionInView(job, index);
    }
  }, [entry?.isIntersecting]);

  return (
    <CardWrapper ref={ref}>
      {hide && (
        <HideJobComponent
          job={job}
          setHide={setHide}
          handleHideJob={handleHideJob}
        />
      )}
      <JobCardComponent
        job={job}
        selectedJob={selectedJob}
        handleSelectJob={handleSelectJob}
        setHide={setHide}
      />
    </CardWrapper>
  );
}

function JobCardComponent(props) {
  const { job, setHide, selectedJob, handleSelectJob } = props;

  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const isBahasa =
    (i18n?.language || "en").split("-")[0].toLowerCase() === "ms";

  const [bookmark, setBookmark] = useState(job?.bookmark);
  const [bookmarkLoading, setBookmarkLoading] = useState(false);

  const [showFullSkills, setShowFullSkills] = useState(false);
  const [showFullBenefits, setShowFullBenefits] = useState(false);

  const isSelected = selectedJob?.some((selected) => selected?.id === job?.id);

  const getJobTypeLabel = (jobTypeValue) => {
    if (!jobTypeValue) return "";
    const key = jobTypeValue
      .toString()
      .toLowerCase()
      .replace(/&/g, "and")
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "");
    return t(`companydetail.job_type_labels.${key}`, {
      defaultValue: jobTypeValue,
    });
  };

  const getStateRegionLabel = (stateRegionValue) => {
    if (!stateRegionValue) return "";
    const key = stateRegionValue
      .toString()
      .toLowerCase()
      .replace(/&/g, "and")
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "");
    return t(`companies.state_region_labels.${key}`, {
      defaultValue: stateRegionValue,
    });
  };

  const convertedGptSummary = useMemo(
    () => convertGptSummary(job, { preferBahasa: isBahasa }),
    [job, isBahasa]
  );

  const truncatedSkills = useMemo(() => {
    const { fullString, truncated, remainingCount } = truncateSkills(job);

    return showFullSkills || !remainingCount ? (
      fullString
    ) : (
      <>
        {truncated}...{" "}
        <ShowMoreText
          onClick={(e) => {
            e.stopPropagation();
            setShowFullSkills(true);
          }}
        >
          +{remainingCount} {t("jobs.more")}
        </ShowMoreText>
      </>
    );
  }, [job?.skills, showFullSkills, t]);

  const truncatedBenefits = useMemo(() => {
    const { fullString, truncated, remainingCount } = truncateBenefits(job);

    return showFullBenefits || !remainingCount ? (
      fullString
    ) : (
      <>
        {truncated}...{" "}
        <ShowMoreText
          onClick={(e) => {
            e.stopPropagation();
            setShowFullBenefits(true);
          }}
        >
          +{remainingCount} {t("jobs.more")}
        </ShowMoreText>
      </>
    );
  }, [job?.company?.benefits, showFullBenefits, t]);

  const company =
    job?.crossedBorder &&
    job?.crossBorderData?.source_country !=
      process.env.NEXT_PUBLIC_JSW_GEOLOCATION.toUpperCase()
      ? job?.crossBorderData?.company
      : job?.company;

  const companyLogo = useMemo(() => {
    return getCompanyLogoImage(company);
  }, [company]);

  const salaryRange = useMemo(() => {
    if (job?.salary === "Undisclosed" || job?.salary === "Undefined")
      return t("jobs.salary_undisclosed");

    return formatSalary(job?.salary, job?.crossBorderData);
  }, [job?.salary, job?.crossBorderData, t]);

  const jobTypeLabel = useMemo(
    () => getJobTypeLabel(job?.jobType),
    [job?.jobType, t]
  );
  const stateRegionLabel = useMemo(
    () => getStateRegionLabel(job?.stateRegion),
    [job?.stateRegion, t]
  );

  const bookmarkIcon = bookmark ? <UnsaveIcon /> : <SaveIcon />;

  function handleClickCard(e) {
    e.stopPropagation();
    e.preventDefault();

    handleTriggerTrackingEvent();
    handleSelectJob(job);
  }

  function handleClickCheckbox(e) {
    e.stopPropagation();
    e.preventDefault();

    handleTriggerTrackingEvent();
    handleSelectJob(job);
  }

  function handleTriggerTrackingEvent() {
    if (isSelected) {
      sendTrackingEvent({
        event: "CE_bulk_job_deselect_individual",
        origin: "fyp_page",
        job_discovery: "rec_profile",
        job_ids: job?.id,
        timestamp: new Date().toISOString(),
        is_selected: "false",
        selection_type: "deselect individual",
      });
    } else {
      sendTrackingEvent({
        event: "CE_bulk_job_select_individual",
        origin: "fyp_page",
        job_discovery: "rec_profile",
        job_ids: job?.id,
        timestamp: new Date().toISOString(),
        is_selected: "true",
        selection_type: "select individual",
      });
    }
  }

  function handleBookmarkJob(e) {
    e.stopPropagation();
    e.preventDefault();

    if (bookmarkLoading) {
      return;
    }

    setBookmarkLoading(true);

    // Toggle bookmark status
    if (!bookmark) {
      dispatch(bookmarkJob({ jobId: job?.id })).then((res) => {
        if (res?.type === types.BOOKMARK_JOB_SUCCEED) {
          toast.success(t("jobs.job_saved_toast"));
          setBookmark(true);
          setBookmarkLoading(false);
        }

        sendTrackingEvent({
          event: "CE_saved-job-for-you",
          origin: "fyp_page",
          job_discovery: "rec_profile",
          "job-id": job?.id,
        });
      });
    } else if (bookmark) {
      dispatch(unBookmarkJob({ jobId: job?.id })).then((res) => {
        if (res?.type === types.UNBOOKMARK_JOB_SUCCEED) {
          toast.success(t("jobs.job_unbookmarked_toast"));
          setBookmark(false);
          setBookmarkLoading(false);
        }

        sendTrackingEvent({
          event: "CE_unsaved-job-for-you",
          origin: "fyp_page",
          job_discovery: "rec_profile",
          "job-id": job?.id,
        });
      });
    }
  }

  function handleViewFullDetails() {
    Cookies.set("click-source", "for_you_page");

    window.open(`jobs/${job?.slug}`, "_blank");
  }

  function handleHideJob(e) {
    e.stopPropagation();
    e.preventDefault();

    const params = {
      jobId: job?.id,
      rejectionReason: "",
    };

    dispatch(updateHideJob(params)).then((res) => {
      if (res?.type === types.HIDE_JOB_SUCCESS) {
        sendTrackingEvent({
          event: "CE_job-hide-click-rec-profile",
          "job-id": job?.id,
        });

        setHide(true);
      }
    });
  }

  return (
    <CardContainer onClick={handleClickCard} selected={isSelected}>
      <BoxContainer flexDirection="column">
        <ImageHeaderContainer>
          <BookmarkButton
            onClick={handleBookmarkJob}
            disabled={bookmarkLoading}
          >
            {bookmarkIcon}
          </BookmarkButton>
          <HeaderImage src={companyLogo} alt={company?.name} />
        </ImageHeaderContainer>

        <JobHeaderContainer>
          <BoxContainer
            justifyContent="space-between"
            alignItems="flex-start"
            gap={"0.5rem"}
          >
            <JobTitleText>{job?.title}</JobTitleText>
            <CheckboxContainer>
              <CheckboxButton
                icon={<UncheckedBox />}
                onClick={handleClickCheckbox}
                checked={isSelected}
                checkedIcon={
                  <CheckedBox>
                    <CheckboxIcon />
                  </CheckedBox>
                }
              />
              <CheckboxHover className="hover-check">
                <CheckboxIcon />
              </CheckboxHover>
            </CheckboxContainer>
          </BoxContainer>

          <BoxContainer gap="0.5rem" alignItems="center">
            <CompanyLogoImage src={companyLogo} alt={company?.name} />
            <CompanyNameText>{company?.name}</CompanyNameText>
          </BoxContainer>
        </JobHeaderContainer>

        <DescriptionContainer>
          <ScrollContainer>
            <DetailContainer>
              <DetailChip type="jobType">{jobTypeLabel}</DetailChip>
              <DetailChip type="state">{stateRegionLabel}</DetailChip>
              <DetailChip type="salary">{salaryRange}</DetailChip>
            </DetailContainer>

            {job?.gptSummary?.length > 0 && (
              <AiSummaryContainer>
                <AiSummaryHeaderText>
                  {t("jobs.for_you_card.ai_generated_summary")}
                </AiSummaryHeaderText>
                <DividerStyled />
                <AiSummaryText>
                  {Array.isArray(convertedGptSummary) ? (
                    <SummaryBulletList>
                      {convertedGptSummary.map((line, index) => (
                        <SummaryBulletItem key={index}>
                          {line}
                        </SummaryBulletItem>
                      ))}
                    </SummaryBulletList>
                  ) : (
                    convertedGptSummary
                  )}
                </AiSummaryText>
              </AiSummaryContainer>
            )}

            {job?.skills?.length > 0 && (
              <JobDetailsText type="skills">
                <b>{t("jobs.for_you_card.skills_label")} </b>
                {truncatedSkills}
              </JobDetailsText>
            )}

            {job?.company?.benefits?.length > 0 && (
              <JobDetailsText type="benefits">
                <b>{t("jobs.for_you_card.benefits_label")} </b>
                {truncatedBenefits}
              </JobDetailsText>
            )}
          </ScrollContainer>
        </DescriptionContainer>
      </BoxContainer>

      <FooterContainer>
        <HideButton onClick={handleHideJob}>
          {t("jobs.for_you_card.hide")}
          <HideIcon />
        </HideButton>
        <MoreDetailButton onClick={() => handleViewFullDetails()}>
          {t("jobs.for_you_card.view_full_details")}
        </MoreDetailButton>
      </FooterContainer>
    </CardContainer>
  );
}

function HideJobComponent(props) {
  const { job, setHide, handleHideJob } = props;

  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [hideReason, setHideReason] = useState(false);
  const [hideReasonValue, setHideReasonValue] = useState("");
  const [hideConfirmation, setHideConfirmation] = useState(false);

  const headerText = useMemo(() => {
    if (hideConfirmation) return t("jobs.for_you_card.hide_thanks");
    if (hideReason) return t("jobs.for_you_card.hide_tell_us_why");
    return t("jobs.for_you_card.job_hidden");
  }, [hideReason, hideConfirmation, t]);

  const forwardActionText = useMemo(() => {
    if (hideConfirmation) return t("jobs.for_you_card.hide_done");
    if (hideReason) return t("jobs.for_you_card.hide_confirm");
    return t("jobs.for_you_card.hide_tell_us_why");
  }, [hideReason, hideConfirmation, t]);

  function handleCloseFeedback() {
    handleHideJob(job);
  }

  function handleUndoHideJob() {
    const params = {
      jobId: job?.id,
    };

    dispatch(undoHideJob(params)).then((res) => {
      res?.type === types.UNDO_HIDE_JOB_SUCCESS && setHide(false);
    });
  }

  function handleHideReason() {
    setHideReason(!hideReason);
  }

  function handleSelectReason(event) {
    setHideReasonValue(event.target.value);
  }

  function handleHideConfirmation() {
    const params = {
      jobId: job?.id,
      rejectionReason: hideReasonValue,
    };

    dispatch(updateFeedbackHideJob(params)).then((res) => {
      if (res?.type === types.FEEDBACK_HIDE_JOB_SUCCESS) {
        setHideConfirmation(true);
        setHideReason(false);
        setHideReasonValue("");
      }
    });
  }

  const handleForwardAction = useCallback(() => {
    if (hideConfirmation) return handleHideJob(job);
    if (hideReason) return handleHideConfirmation();
    return handleHideReason();
  }, [hideConfirmation, hideReason]);

  return (
    <HideJobContainer>
      <BoxContainer
        justifyContent="space-between"
        alignItems="center"
        margin={"0 0 0.5rem"}
      >
        <HideHeaderText>
          {!hideReason && !hideConfirmation && <HideIcon />}
          {headerText}
        </HideHeaderText>
        <CloseButton onClick={handleCloseFeedback}>
          <CloseIcon />
        </CloseButton>
      </BoxContainer>
      {!hideReason && (
        <HideContentText>
          {t("jobs.for_you_card.hide_description")}
        </HideContentText>
      )}

      {hideReason && !hideConfirmation && (
        <OptionContainer
          value={hideReasonValue}
          onChange={(event) => handleSelectReason(event)}
        >
          <OptionLabel
            value="Completely irrelevant to my Profile/Activity"
            control={<RadioStyled />}
            label={t("jobs.for_you_card.hide_reason_irrelevant")}
          />
          <OptionLabel
            value="Not the Experience Level I'm looking for"
            control={<RadioStyled />}
            label={t("jobs.for_you_card.hide_reason_experience")}
          />
          <OptionLabel
            value="Salary is not what I'm looking for"
            control={<RadioStyled />}
            label={t("jobs.for_you_card.hide_reason_salary")}
          />
          <OptionLabel
            value="Others"
            control={<RadioStyled />}
            label={t("jobs.for_you_card.hide_reason_others")}
          />
        </OptionContainer>
      )}

      {hideReason && hideConfirmation && (
        <HideContentText>
          {t("jobs.for_you_card.hide_tune_recommendations")}
        </HideContentText>
      )}

      <HideButtonContainer>
        {!hideReason && (
          <TextButton onClick={handleUndoHideJob}>
            {t("jobs.for_you_card.undo")}
          </TextButton>
        )}
        {hideReason && !hideConfirmation && (
          <TextButton color="purple" onClick={handleHideReason}>
            <LeftArrowIcon />
          </TextButton>
        )}
        <TextButton color="purple" onClick={handleForwardAction}>
          {forwardActionText}
        </TextButton>
      </HideButtonContainer>
    </HideJobContainer>
  );
}
